/**
 * The orb that stands where a spinner stood (Adam, 13 Sep: libraries.dev/orbs).
 *
 * `thinking-orbs` draws a small dotted orb on a 2D canvas with a distinct
 * animation per state, so the indicator says what the agent is doing rather
 * than only that it is busy. The mapping from what we know (the activity
 * status's category and tool) to the orb's nine states lives here and
 * nowhere else, so changing a verb is a one-line edit. The library pauses
 * offscreen and in hidden tabs, renders a still frame under
 * prefers-reduced-motion, and labels itself for screen readers.
 */
import React from 'react'
import { useTheme } from '@mui/material/styles'
import { ThinkingOrb } from 'thinking-orbs'
import type { OrbSize } from 'thinking-orbs'
import type { ActivityStatus } from '../types.js'

export type OrbState = 'working' | 'searching' | 'solving' | 'listening' | 'connecting' | 'weaving' | 'composing' | 'breathing' | 'shaping'

/** The inline preset: the library tunes dot count and speed per size, and 20 is its text-line size. */
export const ORB_INLINE_SIZE: OrbSize = 20

const SEARCH_TOOLS = /search|grep|glob|find|web|lookup|research/i
const SEARCH_COMMANDS = /\bpt (search|vars|chats)\b|\bgrep\b|\bfind\b|\brg\b/

/** The orb at rest, between turns: the loose scatter, held faint (Adam, 13 Sep). */
export const RESTING_ORB_STATE: OrbState = 'working'

/** What the orb should be doing for a given activity status (Adam, 13 Sep:
 *  "working could be idle, connecting when it's doing reasoning"). */
export function orbStateFor(status: Pick<ActivityStatus, 'category' | 'toolName' | 'toolInput'> | null | undefined): OrbState {
  if (!status) return RESTING_ORB_STATE
  switch (status.category) {
    case 'thinking':
      return 'connecting'
    case 'preparing_tool':
    case 'writing':
      return 'composing'
    case 'system':
      return 'breathing'
    case 'tool': {
      const command = typeof status.toolInput?.command === 'string' ? status.toolInput.command : ''
      if ((status.toolName && SEARCH_TOOLS.test(status.toolName)) || SEARCH_COMMANDS.test(command)) return 'searching'
      return 'solving'
    }
    default:
      return 'solving'
  }
}

export interface ActivityOrbProps {
  /** The status the orb speaks for; `state` overrides the mapping. */
  status?: Pick<ActivityStatus, 'category' | 'toolName' | 'toolInput'> | null
  state?: OrbState
  /** The library ships two tuned designs, 20 (inline) and 64 (avatar), not a scale. */
  size?: OrbSize
  /** The ground it sits on; defaults to the MUI palette mode. */
  theme?: 'light' | 'dark'
  label?: string
  /** Multiplier on the design's own speed. */
  speed?: number
  style?: React.CSSProperties
}

export default function ActivityOrb({ status, state, size = ORB_INLINE_SIZE, theme, label, speed, style }: ActivityOrbProps) {
  const mui = useTheme()
  const resolved = state ?? orbStateFor(status)
  return (
    <ThinkingOrb
      state={resolved}
      size={size}
      theme={theme ?? (mui.palette.mode === 'dark' ? 'dark' : 'light')}
      speed={speed}
      aria-label={label}
      data-testid="activity-orb"
      data-orb-state={resolved}
      style={{ flexShrink: 0, display: 'block', ...style }}
    />
  )
}
